import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { geoIdentity, geoPath } from 'd3-geo';
import { IoClose } from 'react-icons/io5';
import malaysiaGeo from '../../map/malaysia/geoBoundaries-MYS-ADM1_simplified.json';
import './MalaysiaMap.css';

const CN_NAMES = {
  Johor: '柔佛',
  Kedah: '吉打',
  Kelantan: '吉兰丹',
  Malacca: '马六甲',
  'Negeri Sembilan': '森美兰',
  Pahang: '彭亨',
  Penang: '槟城',
  Perak: '霹雳',
  Perlis: '玻璃市',
  Selangor: '雪兰莪',
  Terengganu: '登嘉楼',
  Sabah: '沙巴',
  Sarawak: '砂拉越',
  'Kuala Lumpur': '吉隆坡',
  Labuan: '纳闽',
  Putrajaya: '布城',
};

const UNCONTESTED = '#d8d8de';
// Inset-map layout: West Malaysia and East Malaysia get separate projections
// inside one SVG so the South China Sea gap doesn't waste horizontal space.
const WIDTH = 900;
const HEIGHT = 360;
const WEST_BOX = [[10, 10], [340, 350]];
const EAST_BOX = [[360, 10], [890, 350]];
const EAST_NAMES = new Set(['Sabah', 'Sarawak', 'Labuan']);
// Names whose geographic shape is too small to tap on phones — get an invisible
// circular hit-target overlay at their centroid.
const TINY_STATES = new Set(['Perlis', 'Kuala Lumpur', 'Putrajaya', 'Labuan']);

const winnerOf = (allocation) =>
  Object.entries(allocation).reduce(
    (best, [id, seats]) => (seats > best[1] ? [id, seats] : best),
    [null, -1],
  )[0];

const MalaysiaMap = ({ stateData = {}, groupsById = {}, title, titleEn }) => {
  const [selectedName, setSelectedName] = useState(null);
  const [mounted, setMounted] = useState(false);

  const open = Boolean(selectedName);
  const closePanel = () => setSelectedName(null);

  useEffect(() => {
    if (!open) {
      setMounted(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const paths = useMemo(() => {
    const westFC = {
      type: 'FeatureCollection',
      features: malaysiaGeo.features.filter((f) => !EAST_NAMES.has(f.properties.shapeName)),
    };
    const eastFC = {
      type: 'FeatureCollection',
      features: malaysiaGeo.features.filter((f) => EAST_NAMES.has(f.properties.shapeName)),
    };
    const westPath = geoPath(geoIdentity().reflectY(true).fitExtent(WEST_BOX, westFC));
    const eastPath = geoPath(geoIdentity().reflectY(true).fitExtent(EAST_BOX, eastFC));
    return malaysiaGeo.features.map((f) => {
      const name = f.properties.shapeName;
      const data = stateData[name];
      const winId = data && winnerOf(data.allocation);
      const group = winId && groupsById[winId];
      const cn = CN_NAMES[name] || name;
      const gen = EAST_NAMES.has(name) ? eastPath : westPath;
      const [cx, cy] = gen.centroid(f);
      return {
        key: f.properties.shapeID || name,
        name,
        cn,
        d: gen(f),
        cxPct: (cx / WIDTH) * 100,
        cyPct: (cy / HEIGHT) * 100,
        fill: group ? group.color : UNCONTESTED,
        tooltip: group ? `${cn} — ${group.name}` : `${cn} — TBD`,
      };
    });
  }, [stateData, groupsById]);

  const selected = selectedName && stateData[selectedName];
  const selectedCn = selectedName && (CN_NAMES[selectedName] || selectedName);
  const allocationRows = selected
    ? Object.entries(selected.allocation)
        .map(([id, seats]) => ({
          id,
          seats,
          group: groupsById[id],
          pct: (seats / selected.totalSeats) * 100,
        }))
        .sort((a, b) => b.seats - a.seats)
    : [];
  const winningId = selected && winnerOf(selected.allocation);
  const accent = (winningId && groupsById[winningId] && groupsById[winningId].color) || '#cb0b12';

  return (
    <section className="malaysia-map" aria-labelledby={title || titleEn ? 'malaysia-map-heading' : undefined}>
      {(title || titleEn) && (
        <h2 id="malaysia-map-heading" className="malaysia-map__title">
          {title}
          {titleEn && <span className="malaysia-map__title-en">{titleEn}</span>}
        </h2>
      )}
      <div className="malaysia-map__svg-wrap">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Malaysia state-level results"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedName(null);
          }}
        >
          {paths.map((p) => (
            <path
              key={p.key}
              d={p.d}
              fill={p.fill}
              stroke="#fff"
              strokeWidth={selectedName === p.name ? 2 : 0.6}
              className={`malaysia-map__state ${selectedName === p.name ? 'is-selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedName((cur) => (cur === p.name ? null : p.name));
              }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedName((cur) => (cur === p.name ? null : p.name));
                }
              }}
              aria-label={p.tooltip}
            >
              <title>{p.tooltip}</title>
            </path>
          ))}
          {paths.filter((p) => TINY_STATES.has(p.name)).map((p) => (
            <circle
              key={`hit-${p.key}`}
              cx={(p.cxPct / 100) * WIDTH}
              cy={(p.cyPct / 100) * HEIGHT}
              r={14}
              className="malaysia-map__hit"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedName((cur) => (cur === p.name ? null : p.name));
              }}
              aria-label={p.tooltip}
            >
              <title>{p.tooltip}</title>
            </circle>
          ))}
        </svg>

      </div>

      {open && createPortal(
        <>
          <div
            className={`malaysia-map__panel-backdrop ${mounted ? 'open' : ''}`}
            onClick={closePanel}
          />
          <aside
            className={`malaysia-map__panel ${mounted ? 'open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedCn} 议席分配`}
          >
            <div className="malaysia-map__panel-handle" />
            <div className="malaysia-map__panel-header" style={{ borderTopColor: accent }}>
              <div className="malaysia-map__panel-title">
                <span className="malaysia-map__panel-name">{selectedCn}</span>
                <span className="malaysia-map__panel-name-en">{selectedName}</span>
              </div>
              <button
                type="button"
                className="malaysia-map__panel-close"
                onClick={closePanel}
                aria-label="Close"
              >
                <IoClose />
              </button>
            </div>

            <div className="malaysia-map__panel-body">
              <div className="malaysia-map__panel-total">
                <span className="malaysia-map__panel-total-num">{selected.totalSeats}</span>
                <span className="malaysia-map__panel-total-label">总议席 Total Seats</span>
              </div>

              <div className="malaysia-map__bar" aria-label="Seat allocation">
                {allocationRows.map((row) => (
                  <span
                    key={row.id}
                    className="malaysia-map__bar-seg"
                    style={{
                      width: `${row.pct}%`,
                      background: row.group ? row.group.color : UNCONTESTED,
                    }}
                    title={`${row.group ? row.group.name : row.id}: ${row.seats}`}
                  />
                ))}
              </div>

              <ul className="malaysia-map__alloc-list">
                {allocationRows.map((row) => (
                  <li
                    key={row.id}
                    className={`malaysia-map__alloc-row ${row.id === winningId ? 'is-winner' : ''}`}
                  >
                    <span
                      className="malaysia-map__alloc-swatch"
                      style={{ background: row.group ? row.group.color : UNCONTESTED }}
                    />
                    <span className="malaysia-map__alloc-name">
                      {row.group ? row.group.name : row.id}
                      {row.id === winningId && <span className="malaysia-map__alloc-tag">胜</span>}
                    </span>
                    <span className="malaysia-map__alloc-seats">{row.seats}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </>,
        document.body,
      )}

      {!selected && (
        <p className="malaysia-map__hint">点击地图上的州属，查看议席分配</p>
      )}

      <ul className="malaysia-map__legend">
        {Object.entries(groupsById).map(([id, g]) => (
          <li key={id} className="malaysia-map__legend-item">
            <span className="malaysia-map__swatch" style={{ background: g.color }} />
            <span className="malaysia-map__legend-label">{g.name}</span>
            {g.nameEn && <span className="malaysia-map__legend-en">{g.nameEn}</span>}
          </li>
        ))}
      </ul>
      <p className="malaysia-map__credit">
        Boundaries: geoBoundaries (CC-BY 4.0)
      </p>
    </section>
  );
};

export default MalaysiaMap;
